import * as Yup from 'yup';
import { PreviewModalAction } from './helpers';

const confirmChangesValidationSchema = (isRefund, requiresManualRefundAttestation, trailingDocChanges, isAgency) =>
  Yup.object().shape({
    internalNote: Yup.string()
      .ensure()
      .trim()
      .test('minLength20Prod', 'Internal notes must be 20 characters in length or more.', (value) => {
        return process.env.NODE_ENV === 'development' || window.location.hostname.includes('dev') || value.length >= 19;
      })
      .test('noEmail', 'Email addresses are not allowed in internal notes.', (value) => {
        return !value.includes('@');
      })
      .required('Internal notes are required!'),
    allowRefund: Yup.boolean().test('do not delay refunds check', 'Do not delay refunds unless necessary', (value) => {
      if (!isRefund) {
        return true;
      }
      if (!requiresManualRefundAttestation) {
        return true;
      }
      if (requiresManualRefundAttestation && isRefund) {
        return value;
      }
    }),
    signedDocumentsRequiredCheckbox: Yup.boolean().test(
      'signed document attestation',
      'Agent must attest to having the necessary documentation before continuing',
      (value) => (trailingDocChanges?.length && isAgency ? value : true)
    )
  });

const approveRequestedChangesValidationSchema = () =>
  Yup.object().shape({
    preAcceptChangesVerification: Yup.boolean().test(
      'pre accept changes verification',
      'You must attest to having verified the above before continuing',
      (value) => value
    ),
    internalNote: Yup.string()
      .ensure()
      .trim()
      .test('minLength20Prod', 'Internal notes must be 20 characters in length or more.', (value) => {
        return process.env.NODE_ENV === 'development' || window.location.hostname.includes('dev') || value.length >= 19;
      })
      .test('noEmail', 'Email addresses are not allowed in internal notes.', (value) => {
        return !value.includes('@');
      })
      .required('Internal notes are required!')
  });

const requestChangesForApprovalValidationSchema = (changesForApprovalConfig) =>
  Yup.object().shape({
    proofOfElegibilityDocumentsAttestation: Yup.boolean().test(
      'proof of elegibility documents attestation',
      'Agent must attest to having the necessary documentation before continuing',
      (value) => (changesForApprovalConfig?.showProofOfEligibilityDocumentsBlock ? value : true)
    ),
    allHouseholdResidentsIncludedAttestation: Yup.boolean().test(
      'all household residents included attestation',
      'Agent must attest to having included all household residents before continuing',
      (value) => (changesForApprovalConfig?.showAllHouseholdResidentsIncludedBlock ? value : true)
    ),
    internalNote: Yup.string().trim()
  });

const changesForApprovalValidationSchema = (canApproveUWReview, changesForApprovalConfig) =>
  canApproveUWReview
    ? approveRequestedChangesValidationSchema()
    : requestChangesForApprovalValidationSchema(changesForApprovalConfig);

const farmPolicyValidationSchema = (needsUnderwritingReview) =>
  Yup.object().shape({
    farmPolicyAction: Yup.string()
      .oneOf([PreviewModalAction.Draft, PreviewModalAction.Complete])
      .required('Please select a farm policy action'),
    internalNote: Yup.string()
      .ensure()
      .trim()
      .when('farmPolicyAction', {
        is: (action) => action === PreviewModalAction.Complete && !needsUnderwritingReview,
        then: (schema) =>
          schema
            .min(10, 'Internal notes must be 10 characters in length or more.')
            .test('noEmail', 'Email addresses are not allowed in internal notes.', (value) => !value.includes('@'))
            .required('Internal notes are required')
      })
  });

export const validationSchema = (
  isRefund,
  requiresManualRefundAttestation,
  trailingDocChanges,
  isAgency,
  canApproveUWReview,
  changesForApprovalConfig,
  hasDraftActions,
  needsUnderwritingReview
) => {
  if (hasDraftActions) {
    return farmPolicyValidationSchema(needsUnderwritingReview);
  }
  return changesForApprovalConfig?.hasChangesThatNeedApproval
    ? changesForApprovalValidationSchema(canApproveUWReview, changesForApprovalConfig)
    : confirmChangesValidationSchema(isRefund, requiresManualRefundAttestation, trailingDocChanges, isAgency);
};
